import {
  IDateTimeBox,
  IDropDown,
  IFormatableField,
  IUIItem,
  dateTimeUtils,
  logger,
  stringUtils,
} from "cayo.ui";
import { IScheme, IJsonForm, IFormField } from "@cayo/shared/api/types/schema";
import { showError } from "@cayo/shared/components/App/utils/showError";
import { fixMvValue } from "@cayo/shared/components/Form/modelUtils/auxPropsUtils";
import { IFormModel } from "@cayo/shared/components/Form/modelUtils/types";
import {
  FormProps,
  TGenerationRuleObject,
  TGenerationRulesList,
  TPropertiesUsedInGeneration,
} from "@cayo/shared/components/Form/types";
import { evaluateODataLogicalExpression } from "@cayo/shared/scheme/transforms/odataLogicalExpressionEvaluator";
import { ajaxUtils } from "@cayo/shared/utils/ajax";
import { formatDateYearToMs } from "@cayo/shared/utils/date";
import { nameofFactory } from "@cayo/shared/utils/object";
import modelUtils from ".";
import { GR_DELIMITER } from "./constants";
import fieldTypes from "./fieldTypes";
import { getValueConverter } from "./get-value-converter";
import { IObjectPicker } from "../../../api/schema.api";
import { parseGenerationRule } from "../../../scheme/transforms/generation-rule-parser";
import { resolveSoftLink } from "../../ObjectPicker/logic";
import { formComponentTypes } from "../renderers/form-renderers";

const jsonFormNames = nameofFactory<IJsonForm>();

const log = logger.getLogger("build-model");

export const isPicker = (a: IUIItem) => {
  return a.type === "object-picker";
};

export const isPropertyGroup = (a: IUIItem) => {
  return a.type === "property-group";
};

const isDropDown = (a: IUIItem) => {
  return a.type === "drop-down";
};

const isDateTimeComponent = (item: IUIItem) => {
  const { type } = item;
  return type === ("datetimebox" as formComponentTypes) || type === "date-time-box";
};

const timeRegex = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

type getTimeReturnType = { isTime: boolean; value?: string };

const getShortTime = (t: string): getTimeReturnType => {
  const time = t.slice(0, 5);
  const isTime = !!t.slice(0, 5).match(timeRegex);
  const result: getTimeReturnType = { isTime };
  if (isTime) {
    result.value = time;
  }
  return result;
};

type FormItem = IUIItem & { modelProperty?: string; value?: any };

export const getPropValue = (item: FormItem, object: any, ignorePropCase?: boolean) => {
  let result: any = undefined;
  const property = item.modelProperty || item.name!;
  if (!property) {
    return result;
  }
  if (property.indexOf(".") === -1) {
    result = object ? object[property!] : item.value;
  } else if (object) {
    const parts = property.split(".");
    let current = object;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];

      let value = current[part];
      if (value === undefined && ignorePropCase) {
        value = current[stringUtils.toCamelCase(part)!];
      }

      if (value !== undefined) {
        current = value;
      } else {
        current = undefined;
        break;
      }
    }

    result = current;
  }

  return result;
};

const getModelPropertyName = (name: string, parentName?: string) => {
  return parentName ? `${parentName}.${name}` : name;
};

export const getFlatItems = (items: IUIItem[], model?: any, tabIndex?: number) => {
  const tabItem = items.find((i) => i.type === "tab") as IScheme;
  if (tabItem) {
    items = items.filter((i) => i !== tabItem);
    const flatItems: IUIItem[] = [];
    const tabitems = typeof tabIndex === "number" ? [tabItem.items![tabIndex]] : tabItem.items;
    tabitems?.forEach((i) => {
      const currentTab = i as IScheme;
      if (model && currentTab.bindings) {
        const expression = currentTab.bindings["visible"];
        if (expression) {
          const isVisible = expression ? evaluateODataLogicalExpression(expression, model) : true;
          if (!isVisible) {
            return;
          }
        }
      }
      flatItems.push(...currentTab!.items!);
    });

    if (flatItems) {
      items.push(...flatItems);
    }
  }

  return items;
};

export const buildModel = (formProps: FormProps, parentName?: string) => {
  let items = formProps?.items;
  const { object } = formProps;
  let model: IFormModel = {};
  const generationRulesList: TGenerationRulesList = {};
  const propertiesUsedInGeneration: TPropertiesUsedInGeneration = new Set();

  if (!items?.length) {
    return model;
  }

  if (formProps.targetType) {
    model[jsonFormNames("targetType")] = formProps.targetType;
  }

  items = getFlatItems(items);

  const formFields = items.filter((i) => !!i.name && i.type) as IFormField[];
  for (const item of formFields) {
    const name = getModelPropertyName(item.name!, parentName);
    if (item.name !== name) {
      (item as any).modelProperty = name;
    }

    let value = object
      ? modelUtils.resolvePropertyValue(name, object, item.type)
      : (item as any).value;

    if (isPropertyGroup(item)) {
      if (!(item as IFormatableField).formatter) {
        const propertyGroup = {
          ...formProps,
          items: (item as IJsonForm).items,
          targetType: undefined,
        };
        const { model: innerModel } = buildModel(
          propertyGroup,
          (item as any).modelProperty ?? item.name
        );
        model = { ...model, ...innerModel };
        continue;
      }
    } else if (!isPicker(item) && !value) {
      if (isDropDown(item)) {
        const dropDown = item as IDropDown;
        const hasSelection = !!dropDown?.options?.find((o) => o.selected);
        if (!hasSelection && !dropDown.readOnly) {
          if (item.defaultValue) {
            value = item.defaultValue;
          } else if (dropDown.skipAutoSelectFirstOption !== true && dropDown.options?.length > 0) {
            dropDown.options[0].selected = true;
            value = dropDown.options[0].key;
          }
        }
      } else {
        value = value ?? item.defaultValue;
        if (item.type === "number" && typeof value === "string") {
          const convertedValue = Number.parseInt(value);
          if (Number.isNaN(convertedValue)) {
            log.error("Failed to parse value", value);
          } else {
            value = convertedValue;
          }
        }
      }
    }

    // Apply value converter after all type-specific normalization is complete
    // Don't place this code earlier because the value may still be undefined or in its
    // un-normalized form - the converter should always receive the final resolved value
    const vc = getValueConverter(item).to;
    if (vc) {
      value = vc(fixMvValue(value, item.type));
    }

    if (isDateTimeComponent(item)) {
      const dateTimeComp = item as IDateTimeBox;
      if (typeof value === "string") {
        let valueConverted = false;
        if (dateTimeComp.editMode === "onlyTime") {
          const { value: time, isTime } = getShortTime(value);
          if (isTime) {
            value = dateTimeUtils.convertUtcTimeToLocal(time!);
            valueConverted = true;
          }
        }

        if (!valueConverted) {
          value = new Date(value);
        }
      }
    }

    let gr = item.generationRule;

    if (gr) {
      const MINUTES_IN_HOUR = 60;
      const locale = navigator.language;
      const curDate = new Date();
      const formatWithOptions = (options: Intl.DateTimeFormatOptions) =>
        new Intl.DateTimeFormat(locale, options).format(curDate);

      model["CayoAdminUnitName"] = object.CayoAdminUnitName ?? "";
      model["CayoInitiator"] = object.CayoInitiator ?? "";
      model["CayoShortDate"] = formatWithOptions({ dateStyle: "short" });
      model["CayoMediumDate"] = formatWithOptions({ dateStyle: "medium" });
      model["CayoFullDate"] = formatWithOptions({ dateStyle: "full" });
      model["CayoDateTimeShort"] = formatWithOptions({
        dateStyle: "short",
        timeStyle: "short",
      });
      model["CayoDateTimeMedium"] = formatWithOptions({
        dateStyle: "medium",
        timeStyle: "medium",
      });
      model["CayoDateTimeZ"] = formatDateYearToMs(curDate);
      model["CayoClientTimeOffset"] = curDate.getTimezoneOffset() / MINUTES_IN_HOUR;

      if (gr.startsWith("$")) {
        // in case generationRule is a reference to a modelProperty
        const objectProp = gr.slice(1, gr.length);
        gr = object[objectProp];
      }

      if (gr && GR_DELIMITER.test(gr)) {
        // used in setValue to reevaluate field's value
        const ruleObj: TGenerationRuleObject = {
          generationRule: "",
          triggerProperties: new Set(),
        };

        const splittedRule = gr.split(GR_DELIMITER);
        const [generationRule, triggerProperties] = splittedRule;

        ruleObj.generationRule = generationRule;

        // helper objects
        for (const triggerProp of triggerProperties.split(",")) {
          if (triggerProp) {
            ruleObj.triggerProperties.add(triggerProp);
            propertiesUsedInGeneration.add(triggerProp);
          }
        }
        generationRulesList[name] = ruleObj;

        // fill in model with values so the rule generation is correct
        for (const propWatch of propertiesUsedInGeneration) {
          if (!(propWatch in model) && !propWatch.endsWith("Suffix")) {
            if (object[propWatch]) {
              model[propWatch] = object[propWatch];
            } else {
              model[propWatch] = "";
            }
          }
        }

        // skip generation rule evaluation on first model build if object already has a value
        if (model[name]) {
          continue;
        }

        // initial rule generation depends only on doInitialGeneration flag
        const doInitialGeneration = formProps.display?.doInitialGeneration;
        if (doInitialGeneration) {
          value = parseGenerationRule(generationRule, model);
        }
      }
    }

    model[name!] = value;
  }

  if (Object.keys(generationRulesList).length) {
    // in case when field#1 has triggerProperties for field#2, which has triggerProperties for field#3 etc.
    for (const genProp in generationRulesList) {
      const { triggerProperties } = generationRulesList[genProp];
      for (const tP of triggerProperties) {
        if (tP in generationRulesList) {
          generationRulesList[tP].triggerProperties.forEach((v) => triggerProperties.add(v));
        }
      }
    }

    // helper objects, used in generation rules evaluation when updating field's value in setValue
    model["generationRulesList"] = generationRulesList;
    model["propertiesUsedInGeneration"] = propertiesUsedInGeneration;
  }

  const pickerAnnotations = items?.filter(isPicker)?.map<IObjectPicker>((a) => a as IObjectPicker);

  const simplePickers = pickerAnnotations?.filter(
    (l) => l.defaultValue && l.dataType !== fieldTypes.softLink
  );

  simplePickers!.forEach((a) => {
    const picker = a as IObjectPicker;
    const mv =
      typeof picker?.defaultValue === "string"
        ? picker?.defaultValue?.split(";").map((v) => decodeURI(v))
        : undefined;

    if (picker.valueTemplate) {
      model[a.name!] = picker?.defaultValue;
    } else if (picker.type !== fieldTypes.softLink || !picker.defaultValue) {
      model[a.name!] = mv;
    }
  });

  const softLinkPickers = pickerAnnotations?.filter(
    (l) => l.defaultValue && l.dataType === fieldTypes.softLink
  );

  log.debug("buildModel", model);

  return { model, hasAsyncPickers: !!softLinkPickers?.length };
};

export const initPickers = async (formProps: FormProps, model: IFormModel) => {
  const { items } = formProps;

  if (!items?.length) {
    return false;
  }

  const pickerAnnotations = items?.filter(isPicker)?.map<IObjectPicker>((a) => a as IObjectPicker);

  const softLinkPickers = pickerAnnotations?.filter(
    (l) => l.defaultValue && l.dataType === fieldTypes.softLink
  );

  if (softLinkPickers?.length) {
    try {
      for (const p of softLinkPickers) {
        model[p.name!] = await resolveSoftLink(p.queryPath, p.defaultValue!);
      }
    } catch (e) {
      const error = await ajaxUtils.getError(e);
      showError(error.error_description || error.error);
    }

    return true;
  }

  return false;
};
